import {Button, Dialog} from '@bosch/react-frok';
import React, {useState} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {t} from 'i18next';
import {ObjectType} from '../../../models/ObjectType';
import {StorageObject} from '../../../models/StorageObject';
import {
    getEmptyStorageObject,
    getLabelEntryMapInitialized,
    getLabelMapKeys,
    getLabelsAsObjectMap,
    setLabelEntryMap,
    validateLabelEntryMap
} from '../../../utils/StorageObjectUtils';
import {Label} from '../../../models/Label';
import {StorageObjectDTO} from '../../../models/dto/StorageObjectDTO';
import {getEmptyStorageObjectDTO} from '../../../utils/DtoUtils';
import {BreadcrumbsDTO} from '../../../models/dto/BreadcrumbsDTO';
import EventEmitter from 'events';
import {
    FolderMetaDataDialog_changeComment,
    FolderMetaDataDialog_changeDateValidFrom,
    FolderMetaDataDialog_changeDateValidTo,
    FolderMetaDataDialog_changeLabels,
    FolderMetaDataDialog_changeLocales,
    FolderMetaDataDialog_changeOwnerRoles,
    FolderMetaDataDialog_changeRoles,
    FolderMetaDataDialog_changeTags,
    FolderMetaDataDialog_changeVersion,
    FolderMetaDataTabLocales_changeLocales,
    FolderMetaDataTabMain_changeLabels
} from '../../../init/events';
import {getDateStart, getEffectiveDate, getPath, isNullOrEmpty, isValidText} from '../../../utils';
import {Valid} from '../../validation/Valid';
import {FolderMetaDataTabView} from './FolderMetaDataTabView';
import {ContentWrapper, HeaderWarning, HeaderWarningDetails, TextWrapper} from './FolderMetaDataDialog.style';
import {generateIdentifier} from '../../../utils/IdentityUtils';

export interface FolderMetaDataDialogProps {
    readonly item: StorageObject | null;
    readonly name: string;
    readonly breadcrumbs: BreadcrumbsDTO[];
    readonly currentPath: string;
    readonly rolesDataSource: string[];
    readonly parentOwnerRoles: string[];
    readonly parentRoles: string[];
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    onClose: () => void;
    onSave: (storageObjectDTO: StorageObjectDTO, addMore: boolean) => void;
}

const getInitializedStorageObject = (props: FolderMetaDataDialogProps): StorageObject => {
    let initializedItem = ((props.item !== undefined) && props.item) ? cloneDeep(props.item) : getEmptyStorageObject(ObjectType.FOLDER);
    if ((initializedItem) && ((initializedItem.id === undefined) || (isNullOrEmpty(initializedItem.id)))) {
        initializedItem.name = initializedItem.name ?? (props.name ?? '');
        initializedItem.path = decodeURI(props.currentPath);
        initializedItem.fullPath = getPath(initializedItem.path, initializedItem.name);
    }

    initializedItem.labels = getLabelEntryMapInitialized(props.defaultLocale, initializedItem.name, initializedItem.labels);
    initializedItem.isPublic = initializedItem.isPublic ?? false;
    initializedItem.metadata.parentOwnerRoles = props.parentOwnerRoles ?? [];
    initializedItem.metadata.parentRoles = props.parentRoles ?? [];

    return initializedItem;
}

const isValidComment = (storageObject: StorageObject): boolean => {
    if ((!!storageObject) && (isValidText(storageObject.id))) {
        //Task DXF-3159 temporary solution until template rules implementation
        return true;
        // return isValidText(storageObject.metadata.comment ?? '');
    }

    return true;
};

const isValidStorageObject = (storageObject: StorageObject): boolean => {
    return ((!isNullOrEmpty(storageObject.metadata.version ?? ''))
        && validateLabelEntryMap(storageObject.labels)
        && isValidComment(storageObject)) as boolean;
};

/**
 * @name FolderMetaDataDialog
 * @description
 * The dialog to add the metadata of a newly created folder or edited folder - Metadata Tabs
 */
export const FolderMetaDataDialog: React.VFC<FolderMetaDataDialogProps> = (props) => {
    const [storageObjectInput, setStorageObjectInput] = useState<StorageObject>(getInitializedStorageObject(props));
    const [isValid, setIsValid] = useState<boolean>(isValidStorageObject(storageObjectInput));
    //initial state of objects that have to be reverted on reset event
    const [labelsInitialState] = useState<Map<string, Label>>(cloneDeep(getLabelEntryMapInitialized(props.defaultLocale, storageObjectInput.name, storageObjectInput.labels)));
    const [versionInitialState] = useState(cloneDeep(storageObjectInput.metadata.version ?? ''));
    const eventEmitter = new EventEmitter();
    eventEmitter.setMaxListeners(100);

    const handleChangeLabels = (labels: Map<string, Label>) => {
        let storageObject = storageObjectInput;
        storageObject.labels = labels;
        setStorageObjectInput(storageObject);
        let locales = getLabelMapKeys(props.defaultLocale, labels);
        eventEmitter.emit(FolderMetaDataTabMain_changeLabels, labels);
        eventEmitter.emit(FolderMetaDataTabLocales_changeLocales, locales);
        setIsValid(isValidStorageObject(storageObject));
    };

    const handleLocales = (locales: string[]) => {
        let labels = setLabelEntryMap(locales, storageObjectInput.labels);
        handleChangeLabels(labels);
    };

    const handleChangeVersion = (version: string) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.version = version.trim();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObject));
    };

    const handleCommentInput = (comment: string) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.comment = comment.trim();
        setStorageObjectInput(storageObject);
        // setIsValid(isValidStorageObject(props.defaultLocale, nameInput, storageObject)); //Task DXF-3159 temporary solution until template rules implementation
    };

    const handleDateValidFrom = (dateValidFrom?: Date) => {
        if ((dateValidFrom !== undefined) && dateValidFrom) {
            dateValidFrom = getDateStart(dateValidFrom);
        } else {
            dateValidFrom = getEffectiveDate();
        }

        let storageObject = storageObjectInput;
        storageObject.metadata.dateValidFrom = dateValidFrom?.getTime();
        setStorageObjectInput(storageObject);
    };

    const handleDateValidTo = (dateValidTo?: Date) => {
        if ((dateValidTo !== undefined) && dateValidTo) {
            dateValidTo = getDateStart(dateValidTo);
        } else {
            dateValidTo = undefined;
        }

        let storageObject = storageObjectInput;
        storageObject.metadata.dateValidTo = dateValidTo?.getTime();
        setStorageObjectInput(storageObject);
    };

    const handleTags = (tags: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.tags = tags;
        setStorageObjectInput(storageObject);
    };

    const handleRoles = (userRoles: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.roles = userRoles;
        setStorageObjectInput(storageObject);
    };

    const handleOwnerRoles = (ownerRoles: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.ownerRoles = ownerRoles;
        setStorageObjectInput(storageObject);
    };

    const handleSave = (addMore: boolean) => {
        if (!isValid) {
            return;
        }

        let storageObjectDTO = getEmptyStorageObjectDTO();
        let storageObject = cloneDeep(storageObjectInput);
        storageObject.labels = getLabelsAsObjectMap(storageObjectInput.labels);
        storageObjectDTO.item = storageObject;
        props.onSave(storageObjectDTO, addMore);
    };

    eventEmitter.on(FolderMetaDataDialog_changeLabels, handleChangeLabels);
    eventEmitter.on(FolderMetaDataDialog_changeLocales, handleLocales);
    eventEmitter.on(FolderMetaDataDialog_changeVersion, handleChangeVersion);
    eventEmitter.on(FolderMetaDataDialog_changeComment, handleCommentInput);
    eventEmitter.on(FolderMetaDataDialog_changeDateValidFrom, handleDateValidFrom);
    eventEmitter.on(FolderMetaDataDialog_changeDateValidTo, handleDateValidTo);
    eventEmitter.on(FolderMetaDataDialog_changeTags, handleTags);
    eventEmitter.on(FolderMetaDataDialog_changeRoles, handleRoles);
    eventEmitter.on(FolderMetaDataDialog_changeOwnerRoles, handleOwnerRoles);

    const actionButtons = () => {
        return (
            <div id={generateIdentifier()} className='m-dialog__actions'>
                <Button id={generateIdentifier()}
                        key='cancel'
                        mode='secondary'
                        type='button'
                        title={t('GENERAL_CANCEL_LABEL')}
                        label={t('GENERAL_CANCEL_LABEL')}
                        onClick={props.onClose}/>
                <Button id={generateIdentifier()}
                        key='saveExit'
                        icon='upload'
                        mode='secondary'
                        type='button'
                        label={t('GENERAL_SAVE_EXIT_LABEL')}
                        title={t('GENERAL_SAVE_EXIT_LABEL')}
                        onClick={() => handleSave(false)}
                        disabled={(!isValid)}/>
                <Button id={generateIdentifier()}
                        key='saveAddNew'
                        icon='upload'
                        mode='primary'
                        type='button'
                        label={t('GENERAL_SAVE_ADD_NEW_LABEL')}
                        title={t('GENERAL_SAVE_ADD_NEW_LABEL')}
                        onClick={() => handleSave(true)}
                        disabled={(!isValid)}/>
            </div>
        );
    };

    return (
        <Dialog id={generateIdentifier()}
                modal
                open
                title={(!isNullOrEmpty(storageObjectInput.id)) ? t('CREATE_EDIT_FOLDER_DIALOG_TITLE_EDIT') : t('CREATE_EDIT_FOLDER_DIALOG_TITLE_CREATE')}
                onClose={props.onClose}>
            {storageObjectInput.toRemove &&
            <div id={generateIdentifier()}>
                <HeaderWarning id={generateIdentifier()}>
                    {t('CREATE_EDIT_FOLDER_DIALOG_EDIT_EXISTING_ITEM')}
                </HeaderWarning>
                <HeaderWarningDetails id={generateIdentifier()}>
                    {t('CREATE_EDIT_FOLDER_DIALOG_EDIT_MARKED_FOR_DELETION_ITEM')}
                </HeaderWarningDetails>
            </div>
            }
            <ContentWrapper id={generateIdentifier()}>
                <Valid isValid={isValid}
                       message={t('META_DATA_DIALOG_NOT_VALID_CONSTRAINT')}>
                    <FolderMetaDataTabView item={storageObjectInput}
                                           breadcrumbs={props.breadcrumbs}
                                           currentPath={props.currentPath}
                                           parentOwnerRoles={props.parentOwnerRoles}
                                           parentRoles={props.parentRoles}
                                           parentLocales={props.parentLocales}
                                           language={props.language}
                                           defaultLocale={props.defaultLocale}
                                           rolesDataSource={props.rolesDataSource}
                                           labelsInitialState={labelsInitialState}
                                           versionInitialState={versionInitialState}
                                           eventEmitter={eventEmitter}/>
                </Valid>
            </ContentWrapper>
            <TextWrapper id={generateIdentifier()}>
                {`* ${t('GENERAL_MANDATORY_FIELDS_INFO_TEXT')}`}
            </TextWrapper>
            {actionButtons()}
        </Dialog>
    );
}